import * as React from "react";
import { ContextStore } from "../../../react-core/Flux/Variables";
import { BaseComponent } from "../../../react-core/Html/BaseComponent";
import { UserClaim } from "../../../Wrap/Constants/Enums/UserClaim";
import { checkPermission } from "../../Shared/Permission/PermissionHelper";

export type IAdminDashboardProps = React.Props<AdminDashboard>;

interface MenuItem {
    title: string;
    link: string;
    userClaim: UserClaim;
}

interface MenuGroup {
    title: string;
    items: MenuItem[];
    userClaims: UserClaim[];
}

/**
 * Admin menu groups for the admin main(dashboard) page.
 * Extract the data from JSX rendering to trim the code and make the "view" data-driven.
 */
const ADMIN_DASHBOARD_MENU: MenuGroup[] = [
    {
        title: "User management",
        items: [
            {
                link: "/Admin/Users/FindUsers.aspx",
                title: "Find user",
                userClaim: UserClaim.AdviserPortalDluFindUsersCanView,
            },
        ],
        userClaims: [UserClaim.AdviserPortalDluFindUsersCanView],
    },
    {
        title: "Authorisations and corrections",
        items: [
            {
                link: "/Admin/Authorisations/AuthoriseBankAccounts.aspx",
                title: "Authorising bank accounts",
                userClaim: UserClaim.AdviserPortalDluAuthoriseBankAccountsCanView,
            },
            {
                link: "/Admin/Authorisations/AuthoriseDocumentation.aspx",
                title: "Authorise documentations",
                userClaim: UserClaim.AdviserPortalDluAuthoriseDocumentationCanView,
            },
            {
                link: "/admin/Corrections/CorrectionRequest.aspx",
                title: "Error correction: request",
                userClaim: UserClaim.AdviserPortalDluCorrectionRequestCanView,
            },
            {
                link: "/admin/Corrections/CorrectionAuthorise.aspx",
                title: "Error correction: authorise",
                userClaim: UserClaim.AdviserPortalDluCorrectionAuthoriseCanView,
            },
        ],
        userClaims: [
            UserClaim.AdviserPortalDluAuthoriseBankAccountsCanView,
            UserClaim.AdviserPortalDluAuthoriseDocumentationCanView,
            UserClaim.AdviserPortalDluCorrectionRequestCanView,
            UserClaim.AdviserPortalDluCorrectionAuthoriseCanView,
        ],
    },
    {
        title: "Cash",
        items: [
            {
                link: "/admin/cash/manualCashAdjustments.aspx",
                title: "Manual cash adjustment: request",
                userClaim: UserClaim.AdviserPortalDluManualCashAdjustmentsCanView,
            },
            {
                link: "/admin/cash/authoriseCashAdjustments.aspx",
                title: "Manual cash adjustment: authorise",
                userClaim: UserClaim.AdviserPortalDluAuthoriseCashAdjustmentsCanView,
            },
            {
                link: "/admin/cash/AuthoriseWithdrawals.aspx",
                title: "Authorise withdrawals",
                userClaim: UserClaim.AdviserPortalDluAuthoriseWithdrawalsCanView,
            },
            {
                link: "/admin/cash/ReportLog.aspx",
                title: "Reports",
                userClaim: UserClaim.AdviserPortalDluReportLogCanView,
            },
        ],
        userClaims: [
            UserClaim.AdviserPortalDluManualCashAdjustmentsCanView,
            UserClaim.AdviserPortalDluAuthoriseCashAdjustmentsCanView,
            UserClaim.AdviserPortalDluAuthoriseWithdrawalsCanView,
            UserClaim.AdviserPortalDluReportLogCanView,
        ],
    },
    {
        title: "ISA management",
        items: [
            {
                link: "/admin/isa/CashStockMovement.aspx",
                title: "Void and repair",
                userClaim: UserClaim.AdviserPortalDluCashStockMovementCanView,
            },
        ],
        userClaims: [UserClaim.AdviserPortalDluCashStockMovementCanView],
    },
    {
        title: "Issue management",
        items: [
            {
                link: "/issues/logissue.aspx",
                title: "Log issue",
                userClaim: UserClaim.AdviserPortalDluLogIssueCanView,
            },
            {
                link: "/issues/managelog.aspx",
                title: "Manage issues",
                userClaim: UserClaim.AdviserPortalDluManageLogCanView,
            },
            {
                link: "/issues/ViewProjects.aspx",
                title: "View projects",
                userClaim: UserClaim.AdviserPortalDluViewProjectsCanView,
            },
            {
                link: "/issues/AddProject.aspx",
                title: "Add project",
                userClaim: UserClaim.AdviserPortalDluAddProjectCanView,
            },
        ],
        userClaims: [UserClaim.AdviserPortalDluManageLogCanView],
    },
];

/**
 * Test if the page is specific to Admin users
 * @param url - Page's URL
 */
export const isAdminSpecificPage = (url: string = window.location.pathname): boolean => {
    url = url.toLowerCase();

    // starts with "/admin/" and ends with ".aspx"
    if (/\/admin\/.*\.aspx\.*/.test(url)) {
        return true;
    }

    for (const group of ADMIN_DASHBOARD_MENU) {
        for (const item of group.items) {
            if (item.link.toLowerCase() === url) {
                return true;
            }
        }
    }

    // Other pages specified to Admin users
    const otherAdminPages: string[] = [
        "/Research/MarketSummary.aspx",
        "/Documents/Categories.aspx",
        "/Documents/AddDocCode.aspx",
        "/Documents/EditCategoryContent.aspx",
        "/Documents/Upload.aspx",
        "/Documents/EditDocCode.aspx",
        "/Documents/PreviewLiteratureLibraryDocuments.aspx",
        "/issues/logissue.aspx",
        "/issues/managelog.aspx",
        "/issues/ViewProjects.aspx",
        "/issues/AddProject.aspx",
    ];
    for (const link of otherAdminPages) {
        if (url.includes(link.toLowerCase())) {
            return true;
        }
    }

    return false;
};

export class AdminDashboard extends BaseComponent<IAdminDashboardProps, {}> {
    constructor(props: any) {
        super(props);
    }

    public render(): JSX.Element {
        if (!ContextStore.getContext()?.isAdminUser) {
            return null;
        }

        const menuGroups: MenuGroup[] = ADMIN_DASHBOARD_MENU;

        return (
            <div role="main" id="main">
                <h1 className="mb-26">Admin</h1>
                <div className="row admin-cards">
                    {menuGroups.map((group) =>
                        checkPermission(group.userClaims) ? (
                            <div className="col-12 col-md-4 col-sm-6 col-equalise" key={group.title}>
                                <div className="admin-card">
                                    <div className="card-upper">
                                        <h2 className="h3">{group.title}</h2>
                                        {group.items.map((item) =>
                                            checkPermission(item.userClaim) ? (
                                                <div>
                                                    <a href={item.link} className="hyperlink-next">
                                                        {item.title}
                                                    </a>
                                                </div>
                                            ) : null
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : null
                    )}

                    <div className="col-12 col-md-4 col-sm-6 col-equalise">
                        <div className="admin-card">
                            <div className="card-upper">
                                <p className="text-bold text-size-18 mb-12">
                                    More admin activity
                                </p>
                                <p>All other admin activities can be accessed using the Menu.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
